import { Chess } from "chess.js";

type PromotionPiece = "q" | "r" | "b" | "n" | "p";

function promotionOf(uci: string): PromotionPiece | undefined {
  const piece = uci.slice(4, 5);
  return piece === "q" || piece === "r" || piece === "b" || piece === "n" || piece === "p"
    ? piece
    : undefined;
}

export type GameStatus = "playing" | "checkmate" | "stalemate" | "draw" | "insufficient";

export interface TrackerMove {
  legal: boolean;
  san: string;
  uci: string;
  fen: string;
}

export class PositionTracker {
  private readonly chess: Chess;
  private readonly sans: string[] = [];
  private readonly ucis: string[] = [];

  constructor(fen?: string) {
    this.chess = new Chess(fen);
  }

  get fen(): string {
    return this.chess.fen();
  }

  get sideToMove(): "white" | "black" {
    return this.chess.turn() === "w" ? "white" : "black";
  }

  get moves(): string[] {
    return [...this.sans];
  }

  get uciMoves(): string[] {
    return [...this.ucis];
  }

  get inCheck(): boolean {
    return this.chess.inCheck();
  }

  get status(): GameStatus {
    if (this.chess.isCheckmate()) return "checkmate";
    if (this.chess.isStalemate()) return "stalemate";
    if (this.chess.isInsufficientMaterial()) return "insufficient";
    if (this.chess.isDraw()) return "draw";
    return "playing";
  }

  get isOver(): boolean {
    return this.status !== "playing";
  }

  legalMoves(): Array<{ uci: string; san: string; from: string; to: string }> {
    return this.chess.moves({ verbose: true }).map((move) => ({
      uci: `${move.from}${move.to}${move.promotion ?? ""}`,
      san: move.san,
      from: move.from,
      to: move.to,
    }));
  }

  move(uci: string): TrackerMove {
    try {
      const move = this.chess.move({
        from: uci.slice(0, 2),
        to: uci.slice(2, 4),
        promotion: promotionOf(uci),
      });
      if (!move) return { legal: false, san: "", uci, fen: this.chess.fen() };
      const san = move.san;
      const full = `${move.from}${move.to}${move.promotion ?? ""}`;
      this.sans.push(san);
      this.ucis.push(full);
      return { legal: true, san, uci: full, fen: this.chess.fen() };
    } catch {
      return { legal: false, san: "", uci, fen: this.chess.fen() };
    }
  }

  toPgn(headers: Record<string, string> = {}): string {
    const headerBlock = Object.entries(headers)
      .map(([key, value]) => `[${key} "${value.replace(/"/g, '\\"')}"]`)
      .join("\n");
    const body: string[] = [];
    this.sans.forEach((san, index) => {
      if (index % 2 === 0) body.push(`${index / 2 + 1}.`);
      body.push(san);
    });
    const result = this.chess.isCheckmate()
      ? this.chess.turn() === "w"
        ? "0-1"
        : "1-0"
      : this.chess.isDraw()
        ? "1/2-1/2"
        : "*";
    return `${headerBlock}${headerBlock ? "\n\n" : ""}${body.join(" ")} ${result}`;
  }
}
